import type { CSSProperties } from 'react';

import { PushRichTextEditor } from '@features/push-rich-text';
import { charLength } from '@entities/marketing-push/lib';
import { PushImageField } from './blocks';
import type {
  Platform,
  PushContent,
  PushSoftLimits,
  RichSpan,
  RichTextDocument,
} from '../../../types/marketingPushInterfaces';

const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const CARD = 'bg-white rounded-xl shadow-sm p-4 mb-4';
const INPUT = 'h-11 w-full rounded-lg border border-gray-300 px-3 text-sm text-[#2d2c2c]';

const MAX_PUSH_TITLE = 100;
const MAX_PUSH_BODY = 300;
const MAX_INAPP_TITLE = 100;

const PLATFORM_LABEL: Record<Platform, string> = { android: 'Android', ios: 'iOS' };

/* -------------------------------------------------------------------------- */
/* Счётчик символов                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Счётчик с предупреждением об обрезании.
 *
 * Здесь два разных понятия, и путать их нельзя.
 *
 *   максимум — валидация модели (100 и 300 символов), больше сервер не примет;
 *   ориентир — soft_limits с сервера; превышение не ошибка, а предупреждение,
 *              что текст скорее всего обрежется.
 *
 * Запрещать ввод по ориентиру нельзя: реальная граница зависит от устройства,
 * оболочки и размера шрифта в настройках. Но и молчать про обрезание нельзя —
 * иначе половина смысла рассылки не доедет.
 */
function CharCounter({
  value,
  max,
  softLimits,
  field,
  platforms,
}: {
  value: string;
  max: number;
  softLimits?: PushSoftLimits;
  field: 'title' | 'body';
  platforms: Platform[];
}) {
  const length = charLength(value);
  const overMax = length > max;

  const warnings = platforms
    .filter((platform) => {
      const limit = softLimits?.[platform]?.[field];
      return typeof limit === 'number' && length > limit;
    })
    .map((platform) => PLATFORM_LABEL[platform]);

  return (
    <span className="inline-flex items-baseline gap-2 text-xs font-normal">
      {warnings.length > 0 && (
        <span className="text-amber-700">обрежется на {warnings.join(' и ')}</span>
      )}
      <span className={overMax ? 'text-red-600' : 'text-gray-500'}>
        {length}/{max}
      </span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Контент одного языка                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Подписи полей переписаны на однозначные.
 *
 * В старом сервисе поля назывались «Заголовок» / «Тело уведомления» и
 * «Payloads Title» / «Payloads Body», и понять, что уходит в системную
 * плашку, а что показывается внутри приложения, было невозможно без чтения
 * спеки API. Здесь это две группы с явными названиями.
 */
export function PushContentForm({
  content,
  platforms,
  disabled,
  onPatch,
}: {
  content: PushContent;
  platforms: Platform[];
  disabled?: boolean;
  onPatch: (patch: Partial<PushContent>) => void;
}) {
  return (
    <>
      <div className={CARD}>
        <h2 className="mb-1 text-base font-semibold text-[#2d2c2c]">Системное уведомление</h2>
        <p className="mb-4 text-sm text-gray-500">
          Плашка, которую пользователь видит до открытия приложения. Изображение и
          форматирование здесь не показываются.
        </p>

        <div className="mb-4">
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className={FIELD_LABEL}>
              Заголовок <span className="text-red-500">*</span>
            </label>
            <CharCounter
              value={content.push_title}
              max={MAX_PUSH_TITLE}
              softLimits={content.soft_limits}
              field="title"
              platforms={platforms}
            />
          </div>
          <input
            value={content.push_title}
            maxLength={MAX_PUSH_TITLE}
            disabled={disabled}
            onChange={(event) => onPatch({ push_title: event.target.value })}
            className={INPUT}
          />
        </div>

        <div className="mb-1">
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className={FIELD_LABEL}>
              Текст <span className="text-red-500">*</span>
            </label>
            <CharCounter
              value={content.push_body}
              max={MAX_PUSH_BODY}
              softLimits={content.soft_limits}
              field="body"
              platforms={platforms}
            />
          </div>
          <textarea
            value={content.push_body}
            maxLength={MAX_PUSH_BODY}
            rows={3}
            disabled={disabled}
            onChange={(event) => onPatch({ push_body: event.target.value })}
            className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-[#2d2c2c]"
          />
        </div>
        <p className="text-xs text-gray-500">
          Предупреждение об обрезании — ориентир. Точная граница зависит от устройства
          и настроек шрифта.
        </p>
      </div>

      <div className={CARD}>
        <h2 className="mb-1 text-base font-semibold text-[#2d2c2c]">Экран внутри приложения</h2>
        <p className="mb-4 text-sm text-gray-500">
          То, что пользователь увидит, открыв уведомление. Здесь работают форматирование,
          изображение и кнопка действия.
        </p>

        <div className="mb-4">
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className={FIELD_LABEL}>
              Заголовок <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-gray-500">
              {charLength(content.inapp_title)}/{MAX_INAPP_TITLE}
            </span>
          </div>
          <input
            value={content.inapp_title}
            maxLength={MAX_INAPP_TITLE}
            disabled={disabled}
            onChange={(event) => onPatch({ inapp_title: event.target.value })}
            className={INPUT}
          />
        </div>

        <div className="mb-4">
          <label className={FIELD_LABEL}>
            Текст <span className="text-red-500">*</span>
          </label>
          <PushRichTextEditor
            value={content.inapp_body}
            disabled={disabled}
            onChange={(document: RichTextDocument) => onPatch({ inapp_body: document })}
          />
        </div>

        <PushImageField
          value={content.image_url}
          disabled={disabled}
          onChange={(url) => onPatch({ image_url: url })}
        />

        <div className="mb-4">
          <label className={FIELD_LABEL}>Заголовок экрана</label>
          <input
            value={content.navigation_title}
            maxLength={100}
            disabled={disabled}
            onChange={(event) => onPatch({ navigation_title: event.target.value })}
            className={INPUT}
          />
          <p className="mt-1 text-xs text-gray-500">
            Подпись вверху экрана уведомления. Если пусто, приложение подставит своё.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="w-full max-w-[220px]">
            <label className={FIELD_LABEL}>Текст кнопки</label>
            <input
              value={content.button_label}
              maxLength={100}
              disabled={disabled}
              onChange={(event) => onPatch({ button_label: event.target.value })}
              className={INPUT}
            />
          </div>
          <div className="min-w-[240px] flex-1">
            <label className={FIELD_LABEL}>Ссылка кнопки</label>
            <input
              value={content.button_url}
              maxLength={2083}
              placeholder="https://…"
              disabled={disabled}
              onChange={(event) => onPatch({ button_url: event.target.value })}
              className={INPUT}
            />
            {content.button_url && !/^https?:\/\//i.test(content.button_url) && (
              <p className="mt-1 text-xs text-red-600">
                Ссылка должна начинаться с http:// или https://
              </p>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Кнопка появится только если заполнены оба поля.
        </p>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Предпросмотр системной плашки                                               */
/* -------------------------------------------------------------------------- */

/**
 * Плашки Android и iOS нарисованы схематично и намеренно не имитируют
 * настоящие: точная копия создаёт ложное впечатление, будто так и будет
 * выглядеть на устройстве. Показано то, что влияет на решения маркетолога —
 * сколько текста видно до обрезания.
 */
export function PushSystemPreview({
  platform,
  appName,
  title,
  body,
  softLimits,
}: {
  platform: Platform;
  appName: string;
  title: string;
  body: string;
  softLimits?: PushSoftLimits;
}) {
  const limits = softLimits?.[platform];
  const isIos = platform === 'ios';
  const shownTitle = clamp(title || 'Заголовок уведомления', limits?.title);
  const shownBody = clamp(body || 'Текст уведомления', limits?.body);

  return (
    <div>
      <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">
        {PLATFORM_LABEL[platform]}
      </p>

      <div
        className={`flex gap-3 border border-gray-200 bg-white p-3 shadow-sm ${
          isIos ? 'rounded-2xl' : 'rounded-lg'
        }`}
      >
        <div
          aria-hidden
          className={`h-7 w-7 shrink-0 bg-gray-100 ${isIos ? 'rounded' : 'rounded-full'}`}
        />
        <div className="min-w-0 flex-1">
          <div className="mb-[2px] flex justify-between gap-2 text-xs text-gray-500">
            <span className={isIos ? 'uppercase' : ''}>{appName}</span>
            <span>сейчас</span>
          </div>
          <div className="text-sm font-semibold text-[#2d2c2c]" style={truncate(1)}>
            {shownTitle}
          </div>
          <div className="text-sm text-gray-500" style={truncate(isIos ? 2 : 1)}>
            {shownBody}
          </div>
        </div>
      </div>

      {(isClamped(title, limits?.title) || isClamped(body, limits?.body)) && (
        <p className="mt-2 text-xs text-amber-700">
          Текст длиннее, чем обычно показывает {PLATFORM_LABEL[platform]}. Точная граница
          зависит от устройства и настроек шрифта.
        </p>
      )}
    </div>
  );
}

function clamp(value: string, limit?: number): string {
  if (!limit) return value;
  const characters = Array.from(value);
  return characters.length <= limit ? value : `${characters.slice(0, limit).join('')}…`;
}

function isClamped(value: string, limit?: number): boolean {
  return Boolean(limit && charLength(value) > limit);
}

function truncate(lines: number): CSSProperties {
  return {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical' as CSSProperties['WebkitBoxOrient'],
    overflow: 'hidden',
  };
}

/* -------------------------------------------------------------------------- */
/* Предпросмотр экрана внутри приложения                                       */
/* -------------------------------------------------------------------------- */

export function PushInAppPreview({
  navigationTitle,
  title,
  body,
  imageUrl,
  buttonLabel,
}: {
  navigationTitle: string;
  title: string;
  body: RichTextDocument;
  imageUrl: string;
  buttonLabel: string;
}) {
  return (
    <div className="max-w-[340px] overflow-hidden rounded-xl border border-gray-200">
      <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-center text-sm font-medium text-[#2d2c2c]">
        {navigationTitle || 'Уведомление'}
      </div>

      <div className="p-3">
        {imageUrl && (
          <img src={imageUrl} alt="" className="mb-3 block w-full rounded-lg" />
        )}
        <h3 className="mb-2 text-base font-semibold text-[#2d2c2c]">{title || 'Заголовок'}</h3>

        <RenderedDocument document={body} />

        {buttonLabel && (
          <div className="mt-4 rounded-lg bg-blue-600 px-3 py-2 text-center text-sm font-medium text-white">
            {buttonLabel}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Отрисовка документа по тем же правилам, что обязаны соблюдать мобильные
 * клиенты: неизвестный тип блока пропускается, неизвестное оформление
 * игнорируется, но текст выводится. Так предпросмотр не показывает картинку
 * богаче той, что увидит пользователь.
 */
function RenderedDocument({ document }: { document: RichTextDocument }) {
  if (!document?.blocks?.length) {
    return <p className="text-sm text-gray-400">Текст уведомления</p>;
  }

  return (
    <>
      {document.blocks.map((block, index) => {
        if (block.type === 'heading') {
          return block.level === 2 ? (
            <h4 key={index} className="mb-1 mt-3 text-sm font-semibold text-[#2d2c2c]">
              <RenderedSpans spans={block.spans} />
            </h4>
          ) : (
            <h5 key={index} className="mb-1 mt-3 text-sm font-medium text-[#2d2c2c]">
              <RenderedSpans spans={block.spans} />
            </h5>
          );
        }
        if (block.type === 'list') {
          const items = block.items.map((item, itemIndex) => (
            <li key={itemIndex}>
              <RenderedSpans spans={item} />
            </li>
          ));
          return block.ordered ? (
            <ol key={index} className="my-2 list-decimal pl-5 text-sm text-[#2d2c2c]">
              {items}
            </ol>
          ) : (
            <ul key={index} className="my-2 list-disc pl-5 text-sm text-[#2d2c2c]">
              {items}
            </ul>
          );
        }
        if (block.type === 'paragraph') {
          return (
            <p key={index} className="mb-2 text-sm text-[#2d2c2c]">
              <RenderedSpans spans={block.spans} />
            </p>
          );
        }
        // Неизвестный тип блока — пропускаем целиком, как мобильные клиенты.
        return null;
      })}
    </>
  );
}

function RenderedSpans({ spans }: { spans: RichSpan[] }) {
  return (
    <>
      {spans.map((span, index) => {
        const marks = span.marks ?? [];
        const className = [
          marks.includes('bold') ? 'font-semibold' : '',
          marks.includes('italic') ? 'italic' : '',
          marks.includes('underline') ? 'underline' : '',
          marks.includes('strike') ? 'line-through' : '',
        ]
          .filter(Boolean)
          .join(' ');

        if (marks.includes('link') && span.href) {
          return (
            <a
              key={index}
              href={span.href}
              target="_blank"
              rel="noreferrer noopener"
              className={`text-blue-600 ${className}`}
            >
              {span.text}
            </a>
          );
        }
        return (
          <span key={index} className={className}>
            {span.text}
          </span>
        );
      })}
    </>
  );
}
